<template>
  <card-container
    :id="pageId"
    :is-loading="status.isLoading"
    :loading-message="status.spinnerMessage"
    :disabled="!status.isEmailValid || status.isLoading"
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      {{ formTitle }}
    </template>

    <template #content>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> First Name* </label>
        <div class="col-sm-3">
          <sanitized-input
            id="firstName"
            v-model="operator.firstName"
            placeholder="Robert"
            required
          />
        </div>

        <label class="col-sm-2 control-label"> Last Name* </label>
        <div class="col-sm-3">
          <sanitized-input
            id="lastName"
            v-model="operator.lastName"
            placeholder="Wenzel"
            required
          />
        </div>
      </div>

      <div class="row">
        <label class="col-sm-3 control-label pt-10"> Email* </label>
        <div class="col-sm-3">
          <email-input
            v-model="operator.email"
            required
            @update-validation="updateEmailValidation"
          />
        </div>

        <label class="col-sm-2 control-label pt-10"> Team Lead </label>
        <div class="col-sm-3">
          <dropdown-list
            v-model="operator.teamLeadId"
            :options="teamLeads"
            value-key="id"
            label-key="fullName"
            filterable
            clearable
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Phone Number </label>
        <phone-number-input
          id="operator-phone"
          v-model="operator.phoneNumber"
          class="col-sm-3"
          :page-id="pageId"
        />

        <label class="col-sm-2 control-label"> Text Number </label>
        <phone-number-input
          id="operator-text-number"
          v-model="operator.textNumber"
          class="col-sm-3"
          :page-id="pageId"
        />
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Language1 </label>
        <div class="col-sm-3">
          <dropdown-list
            v-model="operator.language1"
            :options="languageList"
          />
        </div>

        <label class="col-sm-2 control-label"> Language2 </label>
        <div class="col-sm-3">
          <dropdown-list
            v-model="operator.language2"
            :options="languageList"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Slack Web Hook </label>
        <div class="col-sm-8">
          <sanitized-input
            id="slackWebHook"
            v-model="operator.slackWebHook"
            placeholder="https://hooks.slack.com/services/"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Slack Alert Web Hook </label>
        <div class="col-sm-8">
          <sanitized-input
            id="slackAlertWebHook"
            v-model="operator.slackAlertWebHook"
            placeholder="https://hooks.slack.com/alert/"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Slack Id </label>
        <div class="col-sm-3">
          <sanitized-input
            id="slackId"
            v-model="operator.slackId"
            placeholder="U8MG6C75Z"
          />
        </div>

        <label class="col-2 control-label"> Sling User ID* </label>
        <div class="col-sm-2 col-3">
          <input
            id="slingUserId"
            v-model="operator.slingUserId"
            type="number"
            required
            class="form-control"
            autocomplete="nope"
          >
        </div>
      </div>

      <div class="row align-items-center">
        <el-tooltip
          content="The priority this operator should be given for handling registration calls."
          placement="top-start"
        >
          <label class="col-sm-3 control-label">Registration Call Priority Override</label>
        </el-tooltip>
        <div class="col-sm-3 d-flex">
          <input
            id="registrationCallPriorityOverride"
            v-model="operator.registrationCallPriorityOverride"
            type="number"
            :min="0"
            :max="100"
            class="form-control"
            autocomplete="nope"
            :disabled="!canEditCallPriorityOverride"
          >
        </div>

        <label class="col-sm-2 control-label"> Default Type* </label>
        <div class="col-sm-3 d-flex">
          <el-radio
            v-model="operator.defaultType"
            class="ban pr-20"
            label="operations"
          >
            Operations
          </el-radio>
        </div>
      </div>

      <div class="row align-items-center">
        <div class="col-sm-3 offset-sm-3 d-flex checkbox-container">
          <el-checkbox
            id="isEnableRegFlowView"
            v-model="operator.isEnableRegFlowView"
          >
            Enable Reg Flow View
          </el-checkbox>
        </div>

        <div class="col-sm-3 d-flex checkbox-container">
          <el-checkbox
            id="isRecordAllCalls"
            v-model="operator.isRecordAllCalls"
            :disabled="!canEditRecordAllCalls"
          >
            Record All Calls
          </el-checkbox>
        </div>

        <div class="col-sm-3 d-flex checkbox-container">
          <el-checkbox
            id="isVerified"
            v-model="operator.isVerified"
          >
            Verified
          </el-checkbox>
        </div>
      </div>
    </template>

    <template #submit-button>
      {{ submitBtnText }}
    </template>

    <template #additional-buttons>
      <p-button
        type="warning"
        round
        @click="backToList()"
      >
        Back to List
      </p-button>
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, reactive, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { OperatorType } from '@/models/operator';
import { languageList } from '@/constants/app';
import registrationCallPriorityOverride from './registrationCallPriority';
import { useGlobal } from '@/use/global';
import { useProfile } from '@/use/profile';
import { OperatorInputType } from '@/gql/graphql';
import { fetchOperator } from '@/services/operators/fetchOperator';
import { createOperator } from '@/services/operators/createOperator';
import { updateOperator } from '@/services/operators/updateOperator';

const pageId = 'add-operator';

const route = useRoute();
const router = useRouter();

const { teamLeads } = useGlobal();
const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();
const { userRoles } = useProfile();

const canEditRecordAllCalls = computed(() => userRoles.isDirector || userRoles.isAdmin || userRoles.isQualityAssurance);
const canEditCallPriorityOverride = computed(() => userRoles.isDirector || userRoles.isAdmin);

const operatorId = ref('');
const operator: Ref<OperatorType> = ref({
  id: '',
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  textNumber: '',
  slackWebHook: '',
  slackAlertWebHook: '',
  slackId: '',
  defaultType: 'operations',
  isDefaultOnly: false,
  registrationCallPriorityOverride: null,
  language1: '',
  language2: '',
  slingUserId: 0,
  isRecordAllCalls: false,
  isRehireEligible: false,
  isEnableRegFlowView: false,
  isVerified: false,
});
const status = reactive({
  isLoading: false,
  spinnerMessage: '',
  isEmailValid: true,
});

const formTitle = computed(() => (!operatorId.value ? 'Add Operator Info' : 'Update Operator Info'));

const submitBtnText = computed(() => (!operatorId.value ? 'Add Operator' : 'Update Operator'));

const updateEmailValidation = (value: boolean) => {
  status.isEmailValid = value;
};

const backToList = (search?: string) => {
  router.push({
    name: 'ManageOperators',
    query: {
      search,
    },
  });
};

const getOperator = async () => {
  status.spinnerMessage = 'Loading Operator Info...';
  status.isLoading = true;
  try {
    const data = await fetchOperator(operatorId.value);

    status.isLoading = false;

    if (!data) {
      await showErrorPopupDialog({
        text: "We can't find operator",
      });

      router.push('/operator/operators');
      return;
    }

    operator.value = {
      id: data.id,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phoneNumber: data.phoneNumber,
      textNumber: data.textNumber,
      slackWebHook: data.slackWebHook,
      slackAlertWebHook: data.slackAlertWebHook,
      slackId: data.slackId,
      defaultType: data.defaultType,
      isDefaultOnly: data.isDefaultOnly,
      registrationCallPriorityOverride: data.registrationCallPriorityOverride ?? null,
      language1: data.language1,
      language2: data.language2,
      slingUserId: data.slingUserId,
      teamLeadId: data.teamLeadId,
      isRecordAllCalls: data.isRecordAllCalls,
      isRehireEligible: data.isRehireEligible,
      isEnableRegFlowView: data.isEnableRegFlowView,
      isVerified: data.isVerified,
    };
  } catch (error) {
    status.isLoading = false;

    const errorText = util.getGraphqlError(error, false);
    await showErrorPopupDialog({
      text: errorText,
    });

    backToList();
  }
};

const submitForm = async () => {
  const postData: OperatorInputType = {
    firstName: operator.value.firstName,
    lastName: operator.value.lastName,
    email: operator.value.email,
    phoneNumber: util.getValidPhoneNumber(operator.value.phoneNumber ?? '') || null,
    textNumber: util.getValidPhoneNumber(operator.value.textNumber ?? '') || null,
    slackWebHook: operator.value.slackWebHook || null,
    slackAlertWebHook: operator.value.slackAlertWebHook || null,
    slackId: operator.value.slackId || null,
    defaultType: operator.value.defaultType,
    isDefaultOnly: operator.value.isDefaultOnly,
    registrationCallPriorityOverride: registrationCallPriorityOverride.prepareFrom({
      registrationCallPriorityOverride: operator.value.registrationCallPriorityOverride ?? null,
    }),
    language1: operator.value.language1,
    language2: operator.value.language2,
    slingUserId: Number(operator.value.slingUserId),
    teamLeadId: operator.value.teamLeadId || null,
    isRecordAllCalls: operator.value.isRecordAllCalls,
    isEnableRegFlowView: operator.value.isEnableRegFlowView,
    isVerified: operator.value.isVerified,
  };

  status.isLoading = true;
  try {
    if (!operatorId.value) {
      await submitCreateOperator(postData);
    } else {
      await submitUpdateOperator(postData);
    }
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  status.isLoading = false;
};

const submitCreateOperator = async (data: OperatorInputType) => {
  status.spinnerMessage = 'Creating Operator Info...';

  await createOperator(data);
  await showSuccessPopupDialog({
    text: 'The operator has been created successfully.',
  });

  backToList(data.slackId!);
};

const submitUpdateOperator = async (data: OperatorInputType) => {
  status.spinnerMessage = 'Updating Operator Info...';
  await updateOperator(operatorId.value, data);

  showSuccessPopupDialog({
    text: 'The operator has been updated successfully.',
  });
};

const resetForm = () => {
  operator.value = {
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    textNumber: '',
    slackWebHook: '',
    slackAlertWebHook: '',
    slackId: '',
    defaultType: 'operations',
    isDefaultOnly: false,
    registrationCallPriorityOverride: null,
    language1: '',
    language2: '',
    slingUserId: 0,
    isRecordAllCalls: false,
    isRehireEligible: false,
    isEnableRegFlowView: false,
  };
};

onMounted(() => {
  util.moveToTop();

  operatorId.value = (route.query?.opId as string) || '';

  if (operatorId.value) {
    getOperator();
  }
});
</script>
